import { Grid, InputAdornment } from '@mui/material';
import currency from 'currency.js';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { lineItemFormText } from '../../constants/lineItems';
import { calculateAdvisoryBudget, calculateDollarBudget, omitCents, round } from '../../util/helpers';
import FormSubsection from '../forms/FormSubsection';
import TextField from '../forms/TextField';
import BreakingFlex from '../shared/BreakingFlex';

interface BudgetRatesFieldsProps {
  bidType: string;
  fixedPricingRate?: string | number;
  children?: React.ReactNode;
  currencyOptions?: currency.Options;
}

function BudgetRatesFields({
  bidType,
  fixedPricingRate,
  children,
  currencyOptions = undefined,
}: BudgetRatesFieldsProps) {
  const form = useFormContext();
  const { getValues, setValue, getFieldState } = form;
  const [dollarBudgetLastTouched, setDollarBudgetLastTouched] = useState(false);

  const getPricingRate = () => fixedPricingRate || getValues('pricingRate');

  const { isDirty: advisoryBudgetIsDirty } = getFieldState('advisoryBudget');
  const { isDirty: dollarBudgetIsDirty } = getFieldState('dollarBudget');

  return (
    <Grid container>
      <FormSubsection title={lineItemFormText.subsectionTitles.budget}>
        <BreakingFlex breakpoint="lg">
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Impression/Engagement Budget"
              name="advisoryBudget"
              onMouseOut={(_, field) => {
                field.onBlur();
              }}
              rules={{
                onBlur: (e) => {
                  const newValue = round(e.target.value);
                  setValue('advisoryBudget', newValue);
                  if (advisoryBudgetIsDirty) {
                    setValue('dollarBudget', calculateDollarBudget(bidType, getPricingRate(), e.target.value));
                  }
                  setDollarBudgetLastTouched(false);
                },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Bonus"
              name="bonus"
              onMouseOut={(_, field) => {
                field.onBlur();
              }}
              rules={{
                onBlur: (e) => setValue('bonus', omitCents(e.target.value)),
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Dollar Budget"
              name="dollarBudget"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onMouseOut={(_, field) => {
                field.onBlur();
              }}
              rules={{
                onBlur: (e) => {
                  const newValue = round(e.target.value);
                  setValue('dollarBudget', newValue);
                  if (dollarBudgetIsDirty) {
                    setValue('advisoryBudget', calculateAdvisoryBudget(bidType, getPricingRate(), e.target.value));
                  }
                  setDollarBudgetLastTouched(true);
                },
              }}
            />
          </Grid>
        </BreakingFlex>
      </FormSubsection>
      {(!fixedPricingRate || children) && (
        <FormSubsection title={lineItemFormText.subsectionTitles.rate}>
          <BreakingFlex breakpoint="md">
            {!fixedPricingRate && (
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Rate"
                  name="pricingRate"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  onMouseOut={(_, field) => {
                    field.onBlur();
                  }}
                  rules={{
                    onBlur: (e) => {
                      if (parseInt(e.target.value, 10) > 0) {
                        setValue('pricingRate', round(e.target.value, currencyOptions));
                        if (dollarBudgetLastTouched) {
                          setValue(
                            'advisoryBudget',
                            calculateAdvisoryBudget(bidType, e.target.value, getValues('dollarBudget'))
                          );
                        } else {
                          setValue(
                            'dollarBudget',
                            calculateDollarBudget(bidType, e.target.value, getValues('advisoryBudget'))
                          );
                        }
                      }
                    },
                  }}
                />
              </Grid>
            )}
            {children}
          </BreakingFlex>
        </FormSubsection>
      )}
    </Grid>
  );
}

export default BudgetRatesFields;
